/**
 * The subscription-length options a saved quote offers, one per duration it priced.
 * A duration needs both an order and an order result: priced but undescribed cannot name
 * its packages.
 */

/** A package as the quote's orders describe one. */
export type QuotePackage = {
  packagesID: number;
  subjectsID: number;
  subjectsName?: string;
  subjectsShortName?: string;
  subscriptionTypesDuration: number;
  promoCode?: string | null;
  [key: string]: any;
};

/** One subscription-length card, in the shape the choose-package step renders. */
export type QuoteDurationOption = {
  subscriptionDuration: number;
  /** "1 Year" / "3 Years", from the duration rather than a name that could be renamed. */
  subscriptionType: string;
  /** Net price of that order, as the quote priced it. */
  price: number;
  /** Shown on a multi-year option only; null for a single year. */
  pricePerYear: string | null;
  packageIds: number[];
  /** Set by the step that preselects a duration, not here. */
  selected?: boolean;
};

const orderList = (quote: any): any[] => (quote && quote.orders) || [];

const orderResultList = (quote: any): any[] => (quote && quote.orderResults) || [];

/** Matched on duration rather than orderNumber, which only indexes the request. */
const packagesForDuration = (quote: any, duration: number): QuotePackage[] => {
  const order = orderList(quote).find(
    (candidate: any) => candidate && candidate.subscriptionTypesDuration === duration
  );

  return (order && order.packagePromos) || [];
};

/** The school-details step matches the selection on this exact string, so the casing matters. */
const durationLabel = (years: number) => (years === 1 ? '1 Year' : years + ' Years');

export const durationOptionsFromQuote = (quote: any): QuoteDurationOption[] => {
  const priced = orderResultList(quote)
    .filter((result: any) => result && result.orderDiscountResult)
    .slice()
    .sort((a: any, b: any) => a.subscriptionTypesDuration - b.subscriptionTypesDuration);

  const options = priced.reduce((built: QuoteDurationOption[], result: any) => {
    const duration = result.subscriptionTypesDuration;
    const packages = packagesForDuration(quote, duration);

    if (packages.length === 0) {
      return built;
    }

    const price = result.orderDiscountResult.totalNetDiscounted;

    return [
      ...built,
      {
        subscriptionDuration: duration,
        subscriptionType: durationLabel(duration),
        price,
        pricePerYear:
          duration > 1 ? 'Price per year £' + (price / duration).toFixed(2) : null,
        packageIds: packages.map((pkg) => pkg.packagesID),
      },
    ];
  }, []);

  // `selected` left unset: the choose-package step preselects by toggling, so seeding it
  // true would make that toggle clear it.
  return options;
};

/** Every package the quote holds, spanning durations so a code still resolves after a switch. */
export const quotePackagesFromQuote = (quote: any): QuotePackage[] =>
  orderList(quote).reduce(
    (packages: QuotePackage[], order: any) => [...packages, ...((order && order.packagePromos) || [])],
    []
  );

/** The database writes the expiry in UTC with no zone, which Date would otherwise read as local time. */
const hasZone = (value: string) => /(Z|[+-]\d{2}:?\d{2})$/i.test(value);

export const quoteHasExpired = (quote: any, now: Date): boolean => {
  const value = quote && quote.expirationDate;

  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }

  const expiry = new Date(hasZone(value) ? value : value + 'Z');

  return !isNaN(expiry.getTime()) && now > expiry;
};

/** getquotedetails refuses an expired quote with this code, the same one it gives an unknown or deleted id. */
export const isQuoteUnavailable = (errors: any): boolean =>
  Array.isArray(errors) && errors.some((error: any) => error && error.messageCode === 'Invalid_QuotesId');
